import { useState } from 'react'
import { ArrowLeft, Eye, EyeOff, Undo2, Redo2, Save, Loader2, BookmarkPlus, X, SlidersHorizontal, Palette, Monitor, Tablet, Smartphone, ExternalLink, Sparkles } from 'lucide-react'
import { Button } from '@brix/ui'
import { useEditor } from '@/hooks/useEditor'
import { useEditorStore, type PreviewDevice } from '@/store/editorStore'
import { useSaveAsTemplate } from '@/hooks/useTemplates'
import { usePage, useUpdatePage } from '@/hooks/usePages'
import { useSite, useUpdateSiteSettings } from '@/hooks/useSites'
import { ImageField } from '@/components/editor/fields'
import { ThemeEditor } from '@/components/ThemeEditor'
import { AiAssistantPanel } from '@/components/editor/AiAssistantPanel'
import { toast } from 'sonner'
import type { PageMeta, SiteSettings } from '@brix/types'

const SITES_URL = (import.meta.env['VITE_SITES_URL'] as string | undefined) ?? 'http://localhost:3001'

const DEVICES: { id: PreviewDevice; icon: typeof Monitor; label: string }[] = [
  { id: 'desktop', icon: Monitor,    label: 'Desktop' },
  { id: 'tablet',  icon: Tablet,     label: 'Tablet (768px)' },
  { id: 'mobile',  icon: Smartphone, label: 'Mobile (375px)' },
]

const CATEGORIES = ['landing', 'portfolio', 'blog', 'ecommerce', 'other'] as const

interface EditorToolbarProps {
  pageId: string
  onBack: () => void
}

// ─── Page Settings Modal ──────────────────────────────────────────────────────

function PageSettingsModal({ pageId, onClose }: { pageId: string; onClose: () => void }) {
  const { data: page } = usePage(pageId)
  const updatePage = useUpdatePage()

  const [title, setTitle] = useState<string | null>(null)
  const [slug, setSlug] = useState<string | null>(null)
  const [meta, setMeta] = useState<PageMeta | null>(null)

  if (!page) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-600 border-t-transparent" />
      </div>
    )
  }

  const currentTitle = title ?? page.title
  const currentSlug  = slug  ?? page.slug
  const currentMeta  = meta  ?? page.meta

  const patchMeta = (patch: Partial<PageMeta>) =>
    setMeta(prev => ({ ...(prev ?? page.meta), ...patch }))

  const save = async () => {
    const patch: { title?: string; slug?: string; meta?: PageMeta } = {}
    if (title  !== null && title  !== page.title) patch.title = title
    if (slug   !== null && slug   !== page.slug)  patch.slug  = slug
    if (meta   !== null) patch.meta = meta
    if (Object.keys(patch).length === 0) { onClose(); return }
    await updatePage.mutateAsync({ id: pageId, ...patch })
    toast.success('Page settings saved')
    onClose()
  }

  const descLen = (currentMeta.description ?? '').length

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">Page settings</h2>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 cursor-pointer">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 space-y-6">

          {/* Page identity */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Page</h3>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-for -- htmlFor+id below already associates this label; the deprecated rule also wants nesting, not used to keep the existing label/input layout */}
              <label htmlFor="page-title" className="mb-1 block text-xs font-medium text-gray-700">Title</label>
              <input
                id="page-title"
                aria-label="Title"
                value={currentTitle}
                onChange={e => setTitle(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder="About us"
              />
            </div>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-for -- htmlFor+id below already associates this label; the deprecated rule also wants nesting, not used to keep the existing label/input layout */}
              <label htmlFor="page-slug" className="mb-1 block text-xs font-medium text-gray-700">URL slug</label>
              <div className="flex items-center rounded-lg border border-gray-200 focus-within:ring-2 focus-within:ring-violet-500 overflow-hidden">
                <span className="pl-3 pr-1 text-sm text-gray-400">/</span>
                <input
                  id="page-slug"
                  aria-label="URL slug"
                  value={currentSlug}
                  onChange={e => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9/-]/g, ''))}
                  className="flex-1 py-2 pr-3 text-sm focus:outline-none"
                  placeholder="about-us"
                />
              </div>
              {page.isHome && (
                <p className="mt-1 text-xs text-amber-600">This is the home page — slug changes don't affect its URL.</p>
              )}
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* SEO */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">SEO</h3>

            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-for -- htmlFor+id below already associates this label; the deprecated rule also wants nesting, not used to keep the existing label/input layout */}
              <label htmlFor="page-meta-title" className="mb-1 block text-xs font-medium text-gray-700">Meta title</label>
              <input
                id="page-meta-title"
                aria-label="Meta title"
                value={currentMeta.title ?? ''}
                onChange={e => patchMeta({ title: e.target.value || undefined })}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder={currentTitle}
              />
              <p className="mt-1 text-xs text-gray-400">Shown in browser tab and search results. Leave blank to use the page title.</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                {/* eslint-disable-next-line jsx-a11y/label-has-for -- htmlFor+id below already associates this label; the deprecated rule also wants nesting, not used to keep the existing label/input layout */}
                <label htmlFor="page-meta-description" className="text-xs font-medium text-gray-700">Meta description</label>
                <span className={`text-xs ${descLen > 155 ? 'text-amber-600' : 'text-gray-400'}`}>{descLen}/160</span>
              </div>
              <textarea
                id="page-meta-description"
                aria-label="Meta description"
                value={currentMeta.description ?? ''}
                onChange={e => patchMeta({ description: e.target.value || undefined })}
                rows={3}
                maxLength={160}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                placeholder="A short description of this page for search engines…"
              />
            </div>

            <div>
              <ImageField
                label="OG image"
                value={currentMeta.ogImage ?? ''}
                onChange={v => patchMeta({ ogImage: v || undefined })}
                folder="og-images"
              />
              <p className="mt-1 text-xs text-gray-400">1200 × 630 px recommended. Shown when sharing this page on social media.</p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
              <div>
                <p className="text-xs font-medium text-gray-700">Hide from search engines</p>
                <p className="text-xs text-gray-400">Adds a noindex tag to this page</p>
              </div>
              <button
                onClick={() => patchMeta({ noIndex: !currentMeta.noIndex })}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                  currentMeta.noIndex ? 'bg-violet-600' : 'bg-gray-200'
                }`}
                role="switch"
                aria-checked={currentMeta.noIndex ?? false}
                aria-label="Hide from search engines"
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${
                    currentMeta.noIndex ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => void save()} loading={updatePage.isPending}>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Site Design Modal ──────────────────────────────────────────────────────
// Same buffered-local-state-then-explicit-save pattern as PageSettingsModal
// above, reusing the shared ThemeEditor (also used by PagesPage's site
// settings panel) so the block editor doesn't need its own theme UI.

function SiteThemeModal({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const { data: site } = useSite(siteId)
  const update = useUpdateSiteSettings()
  const [settings, setSettings] = useState<SiteSettings | null>(null)

  if (!site) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-600 border-t-transparent" />
      </div>
    )
  }

  const current = settings ?? site.settings ?? {}
  const set = (patch: Partial<SiteSettings>) => setSettings({ ...current, ...patch })

  const save = async () => {
    if (!settings) { onClose(); return }
    await update.mutateAsync({ id: siteId, settings: current })
    toast.success('Design saved')
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">Site design</h2>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 cursor-pointer">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          <ThemeEditor value={current} onChange={set} />
          <p className="mt-6 text-xs text-gray-400">
            Changes apply to the whole site, not just this page. The canvas doesn't live-preview theme
            colors/fonts — view the published site to see the result.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => void save()} loading={update.isPending}>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────

export function EditorToolbar({ pageId, onBack }: EditorToolbarProps) {
  const { isDirty, isSaving, previewMode, togglePreview, undo, redo, canUndo, canRedo, save } = useEditor()
  const blocks = useEditorStore((s) => s.blocks)
  const previewDevice = useEditorStore((s) => s.previewDevice)
  const setPreviewDevice = useEditorStore((s) => s.setPreviewDevice)
  const saveAsTemplate = useSaveAsTemplate()

  const { data: page } = usePage(pageId)
  const { data: site } = useSite(page?.siteId ?? '')
  const liveUrl = site && page
    ? page.isHome
      ? `${SITES_URL}/${site.subdomain}`
      : `${SITES_URL}/${site.subdomain}/${page.slug}`
    : null

  const [showTemplateDialog, setShowTemplateDialog] = useState(false)
  const [showPageSettings, setShowPageSettings] = useState(false)
  const [showThemeModal, setShowThemeModal] = useState(false)
  const [showAiPanel, setShowAiPanel] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [templateCategory, setTemplateCategory] = useState<string>('landing')

  async function handleSaveAsTemplate() {
    if (!templateName.trim()) return
    const templateBlocks = blocks.map((b, i) => ({
      type: b.type,
      props: b.props as Record<string, unknown>,
      settings: b.settings as Record<string, unknown>,
      orderIndex: i,
    }))
    await saveAsTemplate.mutateAsync({ name: templateName.trim(), category: templateCategory, blocks: templateBlocks })
    toast.success('Template saved')
    setShowTemplateDialog(false)
    setTemplateName('')
    setTemplateCategory('landing')
  }

  return (
    <>
    <header className="relative flex h-14 items-center justify-between border-b border-gray-200 bg-white px-4 shadow-sm z-10">
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <span className="text-gray-200">|</span>
        <div className="flex gap-1">
          <button
            disabled={!canUndo}
            onClick={() => undo()}
            className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
            title="Undo"
          >
            <Undo2 className="h-4 w-4" />
          </button>
          <button
            disabled={!canRedo}
            onClick={() => redo()}
            className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
            title="Redo"
          >
            <Redo2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {isDirty && !isSaving && (
          <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
        )}
        {isSaving && (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <Loader2 className="h-3 w-3 animate-spin" /> Saving…
          </span>
        )}

        <button
          onClick={() => { setShowPageSettings(true); setShowTemplateDialog(false) }}
          className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
          title="Page settings & SEO"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Page settings
        </button>

        {site && (
          <button
            onClick={() => { setShowThemeModal(true); setShowTemplateDialog(false) }}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
            title="Site design (colors, fonts, theme)"
          >
            <Palette className="h-4 w-4" />
            Design
          </button>
        )}

        {site && (
          <button
            onClick={() => setShowAiPanel((v) => !v)}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
            title="Ask AI to edit this page"
          >
            <Sparkles className="h-4 w-4" />
            Ask AI
          </button>
        )}

        <button
          onClick={() => { setShowTemplateDialog(true); setShowPageSettings(false) }}
          disabled={blocks.length === 0}
          className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
          title="Save as template"
        >
          <BookmarkPlus className="h-4 w-4" />
          Save as template
        </button>

        {previewMode && (
          <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            {DEVICES.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setPreviewDevice(id)}
                title={label}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  previewDevice === id
                    ? 'bg-white text-violet-600 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        )}

        {liveUrl && (
          <a
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            View live
          </a>
        )}

        <button
          onClick={togglePreview}
          className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
        >
          {previewMode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {previewMode ? 'Exit preview' : 'Preview'}
        </button>

        <Button onClick={() => void save()} loading={isSaving} disabled={!isDirty} size="sm">
          <Save className="h-4 w-4" />
          Save
        </Button>
      </div>

      {/* Save as template dialog */}
      {showTemplateDialog && (
        <div className="absolute top-14 right-4 z-50 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Save as template</h3>
            <button
              onClick={() => setShowTemplateDialog(false)}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-for -- htmlFor+id below already associates this label; the deprecated rule also wants nesting, not used to keep the existing label/input layout */}
              <label htmlFor="template-name" className="block text-xs font-medium text-gray-700 mb-1">Name</label>
              <input
                id="template-name"
                aria-label="Name"
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void handleSaveAsTemplate() }}
                placeholder="e.g. Agency Landing Page"
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-for -- htmlFor+id below already associates this label; the deprecated rule also wants nesting, not used to keep the existing label/input layout */}
              <label htmlFor="template-category" className="block text-xs font-medium text-gray-700 mb-1">Category</label>
              <select
                id="template-category"
                value={templateCategory}
                onChange={(e) => setTemplateCategory(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => void handleSaveAsTemplate()}
              loading={saveAsTemplate.isPending}
              disabled={!templateName.trim()}
              size="sm"
              className="w-full"
            >
              Save template
            </Button>
          </div>
        </div>
      )}
    </header>

    {showPageSettings && (
      <PageSettingsModal pageId={pageId} onClose={() => setShowPageSettings(false)} />
    )}
    {showThemeModal && site && (
      <SiteThemeModal siteId={site.id} onClose={() => setShowThemeModal(false)} />
    )}
    {showAiPanel && site && (
      <AiAssistantPanel siteId={site.id} pageId={pageId} onClose={() => setShowAiPanel(false)} />
    )}
    </>
  )
}
